/**
 * Onboarding Wizard v1
 * 
 * 6-step wizard to get merchants to their "aha moment":
 * 1. See a Promise Health snapshot
 * 2. Share it (copy or Slack)
 * 3. Optionally connect Slack
 * 4. Understand what to do next
 * 
 * API calls:
 * - GET /api/integration/status with credentials: include
 * - GET /api/v1/onboarding/snapshot
 * - POST /api/v1/onboarding/progress
 * - POST /api/v1/onboarding/complete
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle, ChevronRight, ChevronLeft, X, 
  ShoppingBag, Slack, Copy, Send, AlertCircle,
  TrendingUp, Target, DollarSign, Users, ArrowRight,
  LogIn, Link2, Clock, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';

import { OnboardingCopy, mapIntegrationStatusToOnboardingState, OnboardingEvents } from '@/copy/onboardingCopy';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Total number of steps (0-indexed, so 6 steps = 0,1,2,3,4,5 + completion)
const TOTAL_STEPS = 6;

export default function OnboardingWizardPage() {
  const navigate = useNavigate();
  
  // Current step (0-5 for steps, 6 for completion)
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  
  // Integration status
  const [integrationState, setIntegrationState] = useState('not_connected');
  const [integrationData, setIntegrationData] = useState(null);
  
  // Snapshot data
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  
  // Slack status
  const [slackConnected, setSlackConnected] = useState(false);
  
  // Events tracking
  const [events, setEvents] = useState({
    snapshot_generated: false,
    playbook_adopted: false,
    report_shared_copy: false,
    report_shared_slack: false,
    slack_connected: false
  });
  
  // Copy state
  const [copied, setCopied] = useState(false);
  
  // Get auth token
  const getToken = () => sessionStorage.getItem('session_token');
  
  // Check onboarding status on mount
  useEffect(() => {
    checkOnboardingStatus();
  }, []);
  
  const checkOnboardingStatus = async () => {
    setLoading(true);
    const token = getToken();
    
    if (!token) {
      // Not logged in - redirect to login
      navigate('/merchant/login');
      return;
    }
    
    try {
      // Check if already completed
      const statusRes = await fetch(`${API_URL}/api/v1/onboarding/status`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        
        if (statusData.completed) {
          // Already completed - show completion screen
          setCurrentStep(TOTAL_STEPS);
          setEvents(statusData.events || {});
        } else {
          // Resume from where they left off
          setCurrentStep(statusData.step_index || 0);
          setEvents(statusData.events || {});
        }
      }
      
      // Load integration status
      await loadIntegrationStatus();
      
    } catch (err) {
      console.error('Error checking onboarding status:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const loadIntegrationStatus = async () => {
    const token = getToken();
    
    try {
      const response = await fetch(`${API_URL}/api/integration/status`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const contentType = response.headers.get('content-type');
      
      if (response.ok) {
        const data = await response.json();
        const state = mapIntegrationStatusToOnboardingState(response.status, data, contentType);
        
        setIntegrationState(state);
        setIntegrationData(data);
        setSlackConnected(data.slack?.connected || false);
        
        // Log event
        await logEvent(OnboardingEvents.INTEGRATION_STATE_SEEN, { state });
      } else {
        const state = mapIntegrationStatusToOnboardingState(response.status, null, contentType);
        setIntegrationState(state);
      }
    } catch (err) {
      console.error('Error loading integration status:', err);
      setIntegrationState('error');
    }
  };
  
  const generateSnapshot = async () => {
    setSnapshotLoading(true);
    const token = getToken();
    
    try {
      const response = await fetch(`${API_URL}/api/v1/onboarding/snapshot?window_days=7`, {
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSnapshot(data);
        
        // Log event and update progress
        await logEvent(OnboardingEvents.SNAPSHOT_GENERATED);
        setEvents(prev => ({ ...prev, snapshot_generated: true }));
        
        toast.success('Snapshot generated!');
      } else {
        toast.error('Failed to generate snapshot');
      }
    } catch (err) {
      console.error('Error generating snapshot:', err);
      toast.error('Failed to generate snapshot');
    } finally {
      setSnapshotLoading(false);
    }
  };
  
  const logEvent = async (eventName, eventData = null) => {
    const token = getToken();
    
    try {
      await fetch(`${API_URL}/api/v1/onboarding/progress`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          step_index: currentStep,
          event: eventName,
          event_data: eventData
        })
      });
    } catch (err) {
      console.error('Error logging event:', err);
    }
  };
  
  const goToStep = async (stepIndex) => {
    setCurrentStep(stepIndex);
    
    // Update backend
    const token = getToken();
    try {
      await fetch(`${API_URL}/api/v1/onboarding/progress`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ step_index: stepIndex })
      });
    } catch (err) {
      console.error('Error updating step:', err);
    }
  };
  
  const nextStep = () => {
    if (currentStep < TOTAL_STEPS) {
      goToStep(currentStep + 1);
    }
  };
  
  const prevStep = () => {
    if (currentStep > 0) {
      goToStep(currentStep - 1);
    }
  };
  
  const completeOnboarding = async () => {
    const token = getToken();
    
    try {
      await fetch(`${API_URL}/api/v1/onboarding/complete`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      setCurrentStep(TOTAL_STEPS);
      toast.success('Onboarding complete! Welcome to Commerce Index.');
    } catch (err) {
      console.error('Error completing onboarding:', err);
    }
  };
  
  const handleCopySnapshot = async () => {
    if (!snapshot) return;
    
    const text = `Commerce Index — Promise Health Snapshot (Last ${snapshot.windowDays || 7} days)

Promise Integrity Score: ${snapshot.score ?? "—"}/100 (${snapshot.deltaText || "—"})
Top driver: ${snapshot.topDriver || "—"} (${snapshot.topDriverRate ?? "—"}% of promises, weighted impact ${snapshot.topDriverImpact ?? "—"})
Benchmark: ${snapshot.benchmarkText || "Benchmark not available yet"}

Estimated monthly leakage: $${snapshot.monthlyLoss ?? "—"}
Potential savings (if breaks reduced by ${snapshot.expectedReduction ?? 25}%): $${snapshot.potentialSavings ?? "—"}

Next actions:
${(snapshot.playbooks || []).map(p => `- ${p}`).join('\n')}

Open dashboard: ${window.location.origin}/dashboard/promise-health`;
    
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      
      await logEvent(OnboardingEvents.REPORT_SHARED_COPY);
      setEvents(prev => ({ ...prev, report_shared_copy: true }));
      
      toast.success('Copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };
  
  const handleSendSlack = async () => {
    if (!slackConnected || !snapshot) return;
    
    const token = getToken();
    
    try {
      const text = `Commerce Index — Promise Health Snapshot (Last ${snapshot.windowDays || 7} days)\n\nPromise Integrity Score: ${snapshot.score ?? "—"}/100\nTop driver: ${snapshot.topDriver || "—"}\nEstimated monthly leakage: $${snapshot.monthlyLoss ?? "—"}`;
      
      await fetch(`${API_URL}/api/v1/integrations/slack/send-report`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ text })
      });
      
      await logEvent(OnboardingEvents.REPORT_SHARED_SLACK);
      setEvents(prev => ({ ...prev, report_shared_slack: true }));
      
      toast.success('Sent to Slack!');
    } catch (err) {
      toast.error('Failed to send to Slack');
    }
  };
  
  const handleAdoptPlaybook = async () => {
    await logEvent(OnboardingEvents.PLAYBOOK_ADOPTED, { driver: snapshot?.topDriver });
    setEvents(prev => ({ ...prev, playbook_adopted: true }));
    toast.success(OnboardingCopy.step4_playbook.adoptedToast);
    nextStep();
  };
  
  const exitToLogin = () => navigate('/merchant/login');
  const exitToDashboard = () => navigate('/dashboard');
  const goToIntegrations = () => navigate('/dashboard/settings/integrations');
  const goToSlackSettings = () => {
    logEvent(OnboardingEvents.SLACK_CONNECT_CLICKED);
    navigate('/dashboard/settings/slack');
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1120] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#3EF1C7] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#9CB0C7]">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Render step content
  const renderStepContent = () => {
    // Completion screen
    if (currentStep === TOTAL_STEPS) {
      return <CompletionStep onNavigate={navigate} />;
    }
    
    switch (currentStep) {
      case 0:
        return (
          <WelcomeStep 
            onContinue={nextStep}
            onSkip={exitToDashboard}
          />
        );
      
      case 1:
        return (
          <ConnectionStep 
            state={integrationState}
            data={integrationData}
            onRetry={loadIntegrationStatus}
            onConnect={goToIntegrations}
            onContinue={() => {
              if (integrationState === 'connected_healthy' || integrationState === 'no_data') {
                generateSnapshot();
                nextStep();
              }
            }}
            onSignIn={exitToLogin}
            onBack={prevStep}
          />
        );
      
      case 2:
        return (
          <SnapshotStep 
            snapshot={snapshot}
            loading={snapshotLoading}
            onContinue={nextStep}
            onSkip={() => goToStep(4)} // Skip to share
            onBack={prevStep}
          />
        );
      
      case 3:
        return (
          <DriverStep 
            snapshot={snapshot}
            onViewPlaybook={nextStep}
            onSkip={nextStep}
            onBack={prevStep}
          />
        );
      
      case 4:
        return (
          <PlaybookStep 
            snapshot={snapshot}
            onAdopt={handleAdoptPlaybook}
            onSkip={nextStep}
            onBack={prevStep}
          />
        );
      
      case 5:
        return (
          <ShareStep 
            snapshot={snapshot}
            slackConnected={slackConnected}
            copied={copied}
            onCopy={handleCopySnapshot}
            onSendSlack={handleSendSlack}
            onFinish={completeOnboarding}
            onConnectSlack={goToSlackSettings}
            onBack={prevStep}
          />
        );
      
      default:
        return null;
    }
  };
  
  return (
    <div className="min-h-screen bg-[#0B1120]" data-testid="onboarding-wizard">
      {/* Header */}
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-lg font-semibold text-white">{OnboardingCopy.global.header}</span>
            {currentStep < TOTAL_STEPS && (
              <Badge variant="outline" className="bg-[#3EF1C7]/10 text-[#3EF1C7] border-[#3EF1C7]/30">
                {OnboardingCopy.global.stepLabel(currentStep + 1, TOTAL_STEPS)}
              </Badge>
            )}
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={exitToDashboard}
            className="text-[#9CB0C7] hover:text-white"
            data-testid="exit-onboarding-btn"
          >
            <X className="w-4 h-4 mr-2" />
            {OnboardingCopy.global.exit}
          </Button>
        </div>
        
        {/* Progress bar */}
        {currentStep < TOTAL_STEPS && (
          <div className="max-w-4xl mx-auto px-6 pb-4">
            <Progress 
              value={(currentStep / TOTAL_STEPS) * 100} 
              className="h-1 bg-[#22304A]"
            />
          </div>
        )}
      </header>
      
      {/* Main content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {renderStepContent()}
      </main>
      
      {/* Footer hint */}
      <footer className="fixed bottom-0 left-0 right-0 py-3 text-center text-xs text-[#6B7C93] bg-[#0B1120]/80 backdrop-blur border-t border-[#22304A]">
        {OnboardingCopy.global.footerHint}
      </footer>
    </div>
  );
}


// =============================================================================
// Step Components
// =============================================================================

function WelcomeStep({ onContinue, onSkip }) {
  const copy = OnboardingCopy.step0_welcome;
  
  return (
    <div className="max-w-2xl mx-auto text-center" data-testid="step-welcome">
      <div className="w-20 h-20 rounded-full bg-[#3EF1C7]/10 flex items-center justify-center mx-auto mb-6">
        <TrendingUp className="w-10 h-10 text-[#3EF1C7]" />
      </div>
      
      <h1 className="text-3xl font-bold text-white mb-3">{copy.title}</h1>
      <p className="text-lg text-[#9CB0C7] mb-8">{copy.subtitle}</p>
      
      <Card className="bg-[#0E1426]/80 border-[#22304A] mb-8 text-left">
        <CardHeader>
          <CardTitle className="text-base text-white">{copy.checklistTitle}</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3">
            {copy.checklist.map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-[#22304A] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs text-[#9CB0C7]">{i + 1}</span>
                </div>
                <span className="text-[#9CB0C7] text-sm">{item}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
      
      <p className="text-xs text-[#6B7C93] mb-6">{copy.note}</p>
      
      <div className="flex items-center justify-center gap-4">
        <Button
          variant="ghost"
          onClick={onSkip}
          className="text-[#9CB0C7]"
          data-testid="skip-onboarding-btn"
        >
          {copy.secondaryCta}
        </Button>
        <Button
          onClick={onContinue}
          className="bg-[#3EF1C7] text-[#0B1120] hover:bg-[#3EF1C7]/90 px-8"
          data-testid="continue-btn"
        >
          {copy.primaryCta}
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}


function ConnectionStep({ state, data, onRetry, onConnect, onContinue, onSignIn, onBack }) {
  const copy = OnboardingCopy.step1_connection;
  const stateCopy = copy.states[state] || copy.states.error;
  
  // Icon mapping
  const stateIcons = {
    needs_auth: LogIn,
    not_connected: Link2,
    no_data: Clock,
    connected_healthy: CheckCircle,
    needs_attention: AlertCircle,
    error: RefreshCw
  };
  
  const StateIcon = stateIcons[state] || AlertCircle;
  
  // Color mapping
  const stateColors = {
    needs_auth: 'text-gray-400',
    not_connected: 'text-blue-400',
    no_data: 'text-blue-400',
    connected_healthy: 'text-green-400',
    needs_attention: 'text-yellow-400',
    error: 'text-gray-400'
  };
  
  return (
    <div className="max-w-2xl mx-auto" data-testid="step-connection">
      <div className="text-center mb-8">
        <div className={`w-16 h-16 rounded-full bg-[#22304A] flex items-center justify-center mx-auto mb-4`}>
          <StateIcon className={`w-8 h-8 ${stateColors[state]}`} />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">{stateCopy.title}</h2>
        <p className="text-[#9CB0C7]">{stateCopy.subtitle}</p>
      </div>
      
      <Card className="bg-[#0E1426]/80 border-[#22304A] mb-6">
        <CardContent className="p-6">
          <p className="text-[#9CB0C7] mb-4">{stateCopy.body}</p>
          
          {/* Stats for connected_healthy */}
          {state === 'connected_healthy' && data && (
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-[#0A1628] rounded-lg p-3 text-center">
                <p className="text-xs text-[#9CB0C7] mb-1">{stateCopy.stats.ordersObserved}</p>
                <p className="text-xl font-bold text-white">
                  {(data.events?.total_views_30d || 0).toLocaleString()}
                </p>
              </div>
              <div className="bg-[#0A1628] rounded-lg p-3 text-center">
                <p className="text-xs text-[#9CB0C7] mb-1">{stateCopy.stats.lastEvent}</p>
                <p className="text-sm font-medium text-white">
                  {data.events?.last_view_ago || 'N/A'}
                </p>
              </div>
              <div className="bg-[#0A1628] rounded-lg p-3 text-center">
                <p className="text-xs text-[#9CB0C7] mb-1">{stateCopy.stats.status}</p>
                <p className="text-sm font-medium text-green-400">
                  {stateCopy.stats.healthy}
                </p>
              </div>
            </div>
          )}
          
          {stateCopy.help && (
            <p className="text-xs text-[#6B7C93]">{stateCopy.help}</p>
          )}
        </CardContent>
      </Card>
      
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={onBack}
          className="text-[#9CB0C7]"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        <div className="flex items-center gap-3">
          {state === 'needs_auth' && (
            <Button onClick={onSignIn} className="bg-[#3EF1C7] text-[#0B1120]">
              {stateCopy.primaryCta}
            </Button>
          )}
          
          {state === 'not_connected' && (
            <Button onClick={onConnect} className="bg-[#3EF1C7] text-[#0B1120]">
              <ShoppingBag className="w-4 h-4 mr-2" />
              {stateCopy.primaryCta}
            </Button>
          )}
          
          {state === 'needs_attention' && (
            <Button onClick={onConnect} className="bg-yellow-500 text-black">
              {stateCopy.primaryCta}
            </Button>
          )}
          
          {state === 'error' && (
            <Button onClick={onRetry} variant="outline">
              {stateCopy.primaryCta}
            </Button>
          )}
          
          {(state === 'connected_healthy' || state === 'no_data') && (
            <Button onClick={onContinue} className="bg-[#3EF1C7] text-[#0B1120]">
              {stateCopy.primaryCta}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}


function SnapshotStep({ snapshot, loading, onContinue, onSkip, onBack }) {
  const copy = OnboardingCopy.step2_snapshot;
  
  if (loading) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="step-snapshot-loading">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-[#3EF1C7]/10 flex items-center justify-center mx-auto mb-4">
            <div className="w-8 h-8 border-2 border-[#3EF1C7] border-t-transparent rounded-full animate-spin"></div>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Generating your snapshot...</h2>
          <p className="text-[#9CB0C7]">Analyzing your promise health data</p>
        </div>
        
        <Card className="bg-[#0E1426]/80 border-[#22304A]">
          <CardContent className="p-6 space-y-4">
            <Skeleton className="h-8 w-1/2" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-2/3" />
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="max-w-2xl mx-auto" data-testid="step-snapshot">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-[#3EF1C7]/10 flex items-center justify-center mx-auto mb-4">
          <Target className="w-8 h-8 text-[#3EF1C7]" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">{copy.title}</h2>
        <p className="text-[#9CB0C7]">{copy.subtitle}</p>
      </div>
      
      {/* Snapshot card */}
      <Card className="bg-[#0E1426]/80 border-[#22304A] mb-6">
        <CardContent className="p-6">
          {/* Score */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-xs text-[#9CB0C7] mb-1">{copy.labels.score}</p>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-white">{snapshot?.score || '—'}</span>
                <span className="text-lg text-[#9CB0C7]">/100</span>
                {snapshot?.deltaText && (
                  <Badge className={`${snapshot.delta >= 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                    {snapshot.deltaText}
                  </Badge>
                )}
              </div>
            </div>
            
            {/* Visual score ring */}
            <div className="relative w-24 h-24">
              <svg className="w-24 h-24 transform -rotate-90">
                <circle
                  cx="48"
                  cy="48"
                  r="40"
                  fill="none"
                  stroke="#22304A"
                  strokeWidth="8"
                />
                <circle
                  cx="48"
                  cy="48"
                  r="40"
                  fill="none"
                  stroke="#3EF1C7"
                  strokeWidth="8"
                  strokeDasharray={`${(snapshot?.score || 0) * 2.51} 251`}
                  strokeLinecap="round"
                />
              </svg>
            </div>
          </div>
          
          {/* Metrics grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-[#0A1628] rounded-lg p-4">
              <p className="text-xs text-[#9CB0C7] mb-1">{copy.labels.topDriver}</p>
              <p className="text-lg font-semibold text-white">{snapshot?.topDriver || '—'}</p>
              <p className="text-xs text-[#6B7C93]">
                {snapshot?.topDriverRate}% rate • {snapshot?.topDriverEvents} events
              </p>
            </div>
            <div className="bg-[#0A1628] rounded-lg p-4">
              <p className="text-xs text-[#9CB0C7] mb-1">{copy.labels.benchmark}</p>
              <p className="text-sm font-medium text-white">{snapshot?.benchmarkText || copy.benchmarkPending}</p>
            </div>
            <div className="bg-[#0A1628] rounded-lg p-4">
              <p className="text-xs text-[#9CB0C7] mb-1">{copy.labels.leakage}</p>
              <p className="text-lg font-semibold text-red-400">
                ${(snapshot?.monthlyLoss || 0).toLocaleString()}
              </p>
            </div>
            <div className="bg-[#0A1628] rounded-lg p-4">
              <p className="text-xs text-[#9CB0C7] mb-1">Potential Savings</p>
              <p className="text-lg font-semibold text-green-400">
                ${(snapshot?.potentialSavings || 0).toLocaleString()}
              </p>
            </div>
          </div>
          
          {snapshot?._demo && (
            <p className="text-xs text-yellow-400/80 text-center">
              ⚠️ Showing demo data. Real metrics will appear as events flow in.
            </p>
          )}
          
          <p className="text-xs text-[#6B7C93] text-center">{copy.note}</p>
        </CardContent>
      </Card>
      
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} className="text-[#9CB0C7]">
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onSkip} className="text-[#9CB0C7]">
            {copy.secondaryCta}
          </Button>
          <Button onClick={onContinue} className="bg-[#3EF1C7] text-[#0B1120]">
            {copy.primaryCta}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}


function DriverStep({ snapshot, onViewPlaybook, onSkip, onBack }) {
  const driver = snapshot?.topDriver || 'Unknown';
  const driverKey = driver.toLowerCase().includes('cancel') ? 'cancellations' :
                    driver.toLowerCase().includes('backorder') ? 'backorders' :
                    driver.toLowerCase().includes('late') || driver.toLowerCase().includes('partial') ? 'late_partial' :
                    driver.toLowerCase().includes('support') ? 'support' : 'default';
  
  const copy = OnboardingCopy.step3_topDriver;
  const insight = copy.insights[driverKey];
  
  return (
    <div className="max-w-2xl mx-auto" data-testid="step-driver">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-8 h-8 text-red-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">{copy.title(driver)}</h2>
        <p className="text-[#9CB0C7]">{copy.subtitle}</p>
      </div>
      
      <Card className="bg-[#0E1426]/80 border-[#22304A] mb-6">
        <CardContent className="p-6">
          {/* Metrics */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center">
              <p className="text-xs text-[#9CB0C7] mb-1">{copy.metrics.events}</p>
              <p className="text-2xl font-bold text-white">{snapshot?.topDriverEvents || '—'}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-[#9CB0C7] mb-1">{copy.metrics.weightedImpact}</p>
              <p className="text-2xl font-bold text-white">{snapshot?.topDriverImpact || '—'}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-[#9CB0C7] mb-1">{copy.metrics.ratePer100}</p>
              <p className="text-2xl font-bold text-white">{snapshot?.topDriverRate || '—'}%</p>
            </div>
          </div>
          
          {/* Insight box */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-400 mb-2">{insight.title}</h3>
            <p className="text-sm text-[#9CB0C7]">{insight.body}</p>
          </div>
        </CardContent>
      </Card>
      
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} className="text-[#9CB0C7]">
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onSkip} className="text-[#9CB0C7]">
            {copy.secondaryCta}
          </Button>
          <Button onClick={onViewPlaybook} className="bg-[#3EF1C7] text-[#0B1120]">
            {copy.primaryCta}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}


function PlaybookStep({ snapshot, onAdopt, onSkip, onBack }) {
  const copy = OnboardingCopy.step4_playbook;
  const driver = snapshot?.topDriver || 'Unknown';
  const playbooks = snapshot?.playbooks || [];
  
  return (
    <div className="max-w-2xl mx-auto" data-testid="step-playbook">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-[#7A5CFF]/10 flex items-center justify-center mx-auto mb-4">
          <Target className="w-8 h-8 text-[#7A5CFF]" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">{copy.title}</h2>
        <p className="text-[#9CB0C7]">{copy.subtitle}</p>
      </div>
      
      <Card className="bg-[#0E1426]/80 border-[#22304A] mb-6">
        <CardHeader>
          <CardTitle className="text-base text-white">{copy.cardTitle(driver)}</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3 mb-6">
            {playbooks.map((pb, i) => (
              <li key={i} className="flex items-start gap-3 p-3 bg-[#0A1628] rounded-lg">
                <div className="w-6 h-6 rounded-full bg-[#7A5CFF]/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-medium text-[#7A5CFF]">{i + 1}</span>
                </div>
                <span className="text-sm text-white">{pb}</span>
              </li>
            ))}
          </ul>
          
          <p className="text-xs text-[#6B7C93]">{copy.help}</p>
        </CardContent>
      </Card>
      
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} className="text-[#9CB0C7]">
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={onSkip} className="text-[#9CB0C7]">
            {copy.buttons.skip}
          </Button>
          <Button onClick={onAdopt} className="bg-[#7A5CFF] hover:bg-[#7A5CFF]/90">
            <CheckCircle className="w-4 h-4 mr-2" />
            {copy.buttons.adopt}
          </Button>
        </div>
      </div>
    </div>
  );
}


function ShareStep({ snapshot, slackConnected, copied, onCopy, onSendSlack, onFinish, onConnectSlack, onBack }) {
  const copy = OnboardingCopy.step5_share;
  
  return (
    <div className="max-w-2xl mx-auto" data-testid="step-share">
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-[#3EF1C7]/10 flex items-center justify-center mx-auto mb-4">
          <Send className="w-8 h-8 text-[#3EF1C7]" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">{copy.title}</h2>
        <p className="text-[#9CB0C7]">{copy.subtitle}</p>
      </div>
      
      <Card className="bg-[#0E1426]/80 border-[#22304A] mb-6">
        <CardContent className="p-6">
          {/* Snapshot summary */}
          {snapshot && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="bg-[#0A1628] rounded-lg p-3">
                <p className="text-xs text-[#9CB0C7]">Score</p>
                <p className="text-xl font-bold text-white">{snapshot.score}/100</p>
              </div>
              <div className="bg-[#0A1628] rounded-lg p-3">
                <p className="text-xs text-[#9CB0C7]">Top Driver</p>
                <p className="text-sm font-medium text-white truncate">{snapshot.topDriver}</p>
              </div>
              <div className="bg-[#0A1628] rounded-lg p-3">
                <p className="text-xs text-[#9CB0C7]">Monthly Loss</p>
                <p className="text-sm font-medium text-red-400">${snapshot.monthlyLoss?.toLocaleString()}</p>
              </div>
              <div className="bg-[#0A1628] rounded-lg p-3">
                <p className="text-xs text-[#9CB0C7]">Benchmark</p>
                <p className="text-xs font-medium text-white truncate">{snapshot.benchmarkText || 'Pending'}</p>
              </div>
            </div>
          )}
          
          {/* Action buttons */}
          <div className="flex flex-wrap gap-3 mb-4">
            <Button
              variant="outline"
              onClick={onCopy}
              className="bg-blue-500/10 border-blue-500/30 hover:bg-blue-500/20 text-blue-400"
            >
              {copied ? <CheckCircle className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              {copied ? 'Copied!' : copy.actions.copy}
            </Button>
            
            <Button
              variant="outline"
              onClick={slackConnected ? onSendSlack : onConnectSlack}
              disabled={!slackConnected}
              className={slackConnected 
                ? "bg-green-500/10 border-green-500/30 hover:bg-green-500/20 text-green-400"
                : "opacity-50 cursor-not-allowed"
              }
            >
              <Slack className="w-4 h-4 mr-2" />
              {slackConnected ? copy.actions.slack : 'Connect Slack first'}
            </Button>
          </div>
          
          {!slackConnected && (
            <p className="text-xs text-[#6B7C93]">{copy.slackNotConnected}</p>
          )}
          
          <p className="text-xs text-[#6B7C93] mt-4">{copy.note}</p>
        </CardContent>
      </Card>
      
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} className="text-[#9CB0C7]">
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        
        <div className="flex items-center gap-3">
          {!slackConnected && (
            <Button variant="ghost" onClick={onConnectSlack} className="text-[#9CB0C7]">
              {copy.secondaryCta}
            </Button>
          )}
          <Button onClick={onFinish} className="bg-[#3EF1C7] text-[#0B1120]">
            {copy.primaryCta}
            <CheckCircle className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}


function CompletionStep({ onNavigate }) {
  const copy = OnboardingCopy.completion;
  
  return (
    <div className="max-w-2xl mx-auto text-center" data-testid="step-completion">
      <div className="w-24 h-24 rounded-full bg-[#3EF1C7]/10 flex items-center justify-center mx-auto mb-6">
        <CheckCircle className="w-12 h-12 text-[#3EF1C7]" />
      </div>
      
      <h1 className="text-3xl font-bold text-white mb-3">{copy.title}</h1>
      <p className="text-lg text-[#9CB0C7] mb-8">{copy.subtitle}</p>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Button
          variant="outline"
          className="h-auto py-4 flex flex-col items-center gap-2 bg-[#0E1426]/80 border-[#22304A] hover:border-[#3EF1C7]/30"
          onClick={() => onNavigate('/dashboard/promise-health')}
          data-testid="go-promise-health-btn"
        >
          <TrendingUp className="w-6 h-6 text-[#3EF1C7]" />
          <span className="text-white">{copy.buttons.promiseHealth}</span>
        </Button>
        
        <Button
          variant="outline"
          className="h-auto py-4 flex flex-col items-center gap-2 bg-[#0E1426]/80 border-[#22304A] hover:border-[#3EF1C7]/30"
          onClick={() => onNavigate('/dashboard/exception-radar')}
          data-testid="go-exception-radar-btn"
        >
          <AlertCircle className="w-6 h-6 text-yellow-400" />
          <span className="text-white">{copy.buttons.exceptionRadar}</span>
        </Button>
        
        <Button
          variant="outline"
          className="h-auto py-4 flex flex-col items-center gap-2 bg-[#0E1426]/80 border-[#22304A] hover:border-[#3EF1C7]/30"
          onClick={() => onNavigate('/dashboard/settings/slack')}
          data-testid="go-slack-settings-btn"
        >
          <Slack className="w-6 h-6 text-[#7A5CFF]" />
          <span className="text-white">{copy.buttons.slackSettings}</span>
        </Button>
      </div>
      
      <p className="text-xs text-[#6B7C93]">{copy.note}</p>
    </div>
  );
}
